import React, { useState } from "react";
import {
  Dialog,
  Grid,
  Typography,
  makeStyles,
  withStyles,
  Card,
  CardHeader,
  CardContent,
  InputLabel,
  Paper,
  Chip,
  IconButton,
  Box,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import CloseIcon from "@material-ui/icons/Close";
import { Trans, useTranslation } from "react-i18next";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import {
  getAttribute,
  getAttributeValue,
} from "../../../../Utilities/Functions";
import styles from "./styles";
import { Markup } from "interweave";
import CustomButton from "../../../../Component/CustomButton";

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const ViewRequest = (props) => {
  const { open, documentRequest = {}, onClose } = props;
  const { t } = useTranslation();
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [isDialogOpen, setIsDialogOpen] = useState(open);

  const { entity_id, client_entity_id, clientRecordName, request } =
    documentRequest;

  const getRecordName = (record) => {
    const { entity, associations } = record;
    let name = getAttributeValue(getAttribute(entity.attributes, "3"));
    if (associations && associations.length > 0) {
      const associationNames = associations
        .map((a) => a.associationTypeName)
        .sort();
      return (
        <span>
          <Link to={`/basicinfo?id=${entity.id}`} target="_blank">
            {name}
          </Link>
          <Typography>
            {`(${Array.from(new Set(associationNames)).join(",")})`}
          </Typography>
        </span>
      );
    }
    return name;
  };

  const renderHeader = () => {
    const { requestType, records = [] } = request;
    const client = records.find((r) => r.entity.id === entity_id);
    const { clientType } = client || {};
    const clientName = client ? getRecordName(client) : "";

    return (
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputLabel className={classes.inputLabel}>
                {t("labels.record.documentRequest.viewRequest.requestType")}
              </InputLabel>
              <Typography>
                {requestType === "1"
                  ? t(
                      "labels.record.documentRequest.steps.requestTypeStep.genericRequest",
                    )
                  : t(
                      "labels.record.documentRequest.steps.requestTypeStep.specificRequest",
                    )}
              </Typography>
              {client_entity_id !== entity_id ? (
                <div>
                  <i>
                    {t(
                      "labels.record.documentRequest.table.headers.requestedFrom",
                    )}
                    &nbsp;
                    <Link
                      to={{ search: `?id=${client_entity_id}` }}
                      target="_blank"
                    >
                      {clientRecordName}
                    </Link>
                  </i>
                </div>
              ) : (
                ""
              )}
            </Grid>
            {requestType === "1" && (
              <Grid item xs={10}>
                <InputLabel className={classes.inputLabel}>
                  {t("labels.record.documentRequest.viewRequest.clientName")}
                </InputLabel>
                <Typography>{clientName}</Typography>
              </Grid>
            )}
            {requestType === "1" && (
              <Grid item xs={2}>
                <InputLabel className={classes.inputLabel}>
                  {t("labels.record.documentRequest.viewRequest.clientType")}
                </InputLabel>
                <Typography>{clientType.name}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderAssociatedRecords = () => {
    const { records = [] } = request;
    const associatedRecords = records.filter((r) => r.entity.id !== entity_id);
    if (associatedRecords.length === 0) {
      return;
    }
    return (
      <Card variant="outlined">
        <CardHeader subheader={"Associated Records"}></CardHeader>
        <CardContent>
          {associatedRecords.map((r, index) => {
            const { clientType } = r;
            const recordName = getRecordName(r);
            return (
              <Grid container spacing={2} key={index}>
                <Grid item xs={10}>
                  <InputLabel className={classes.inputLabel}>
                    {t("labels.record.documentRequest.viewRequest.recordName")}
                  </InputLabel>
                  <Typography>{recordName}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <InputLabel className={classes.inputLabel}>
                    {t("labels.record.documentRequest.viewRequest.clientType")}
                  </InputLabel>
                  <Typography>{clientType.name}</Typography>
                </Grid>
              </Grid>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  const renderRecords = () => {
    const { records = [] } = request;
    return records.map((r, index) => (
      <Grid item xs={12} key={index}>
        {renderRecord(r)}
      </Grid>
    ));
  };

  const renderRecord = (record) => {
    const {
      clientType,
      documentTypes = {},
      clientUser = {},
      userMessage,
    } = record;
    const { username, userEmail } = clientUser;
    const { coreDocuments = [] } = documentTypes || {};
    const recordName = getRecordName(record);
    if (coreDocuments.length === 0) {
      return;
    }
    return (
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <InputLabel className={classes.inputLabel}>
                {t("labels.record.documentRequest.viewRequest.recordName")}
              </InputLabel>
              <Typography>{recordName}</Typography>
            </Grid>
            <Grid item xs={2}>
              <InputLabel className={classes.inputLabel}>
                {t("labels.record.documentRequest.viewRequest.clientType")}
              </InputLabel>
              <Typography>{clientType.name}</Typography>
            </Grid>

            {renderDocuments(documentTypes)}
            <Grid item xs={12}>
              <InputLabel className={classes.inputLabel}>
                {t(
                  "labels.record.documentRequest.viewRequest.clientUserMessage",
                )}
              </InputLabel>
              <Box
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  backgroundColor: "rgb(243, 246, 249)",
                  padding: 16,
                }}
              >
                <Paper
                  variant="outlined"
                  square
                  style={{ padding: 8, width: "100%" }}
                >
                  {userMessage ? <Markup content={userMessage} /> : "-"}
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderDocuments = (documentTypes) => {
    const { coreDocuments = [], documentNames } = documentTypes || {};
    if (coreDocuments.length > 0) {
      return (
        <Grid container item xs={12}>
          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>
              {t(
                "labels.record.documentRequest.steps.documentSelectionStep.coreDocuments",
              )}
            </InputLabel>
            <Paper component="ul" className={classes.chipArray} elevation={0}>
              {coreDocuments.map((d) => {
                return (
                  <li key={d.id}>
                    <Chip
                      label={d.name}
                      className={classes.docName}
                      size="small"
                    />
                  </li>
                );
              })}
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>
              {t(
                "labels.record.documentRequest.steps.documentSelectionStep.documentNames",
              )}
            </InputLabel>
            <Box
              style={{
                display: "flex",
                flexWrap: "wrap",
                backgroundColor: "rgb(243, 246, 249)",
                padding: 16,
              }}
            >
              <Paper
                variant="outlined"
                square
                style={{ padding: 8, width: "100%" }}
              >
                {documentNames ? <Markup content={documentNames} /> : "-"}
              </Paper>
            </Box>
          </Grid>
        </Grid>
      );
    }
  };

  const renderUser = (record) => {
    const { clientUser = {}, userMessage } = record;
    const { username, userEmail } = clientUser;
    return (
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputLabel className={classes.inputLabel}>
                {t("labels.record.documentRequest.viewRequest.clientUserName")}
              </InputLabel>
              <Typography>{`${username}(${userEmail})`}</Typography>
            </Grid>
            {userMessage && (
              <Grid item xs={12}>
                <InputLabel className={classes.inputLabel}>
                  {t(
                    "labels.record.documentRequest.viewRequest.clientUserMessage",
                  )}
                </InputLabel>
                <Typography>{userMessage || "-"}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const cancel = () => {
    setIsDialogOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const { requestType } = request;
  const renderRequest = () => {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          {renderHeader()}
        </Grid>
        {requestType === "1" && (
          <Grid item xs={12}>
            {renderAssociatedRecords()}
          </Grid>
        )}
        {requestType === "2" && renderRecords()}
        <Grid item xs={12}>
          {renderUser(request)}
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog
      disableBackdropClick
      scroll={"paper"}
      onClose={cancel}
      aria-labelledby="add-document-modal"
      fullWidth
      open={isDialogOpen}
      maxWidth="md"
    >
      <DialogTitle id="add-document-modal" onClose={cancel}>
        <Typography className={classes.panelTitle}>
          {t("labels.record.documentRequest.viewRequest.title")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>{renderRequest()}</DialogContent>
      <DialogActions>
        <CustomButton 
          variant="secondary" 
          onClick={cancel}
        >
          {t("labels.common.close")}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles, { withTheme: true })(ViewRequest);
